import {useState} from 'react';
import {format} from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {CODE_PERMISSIONS} from '@common/permissions';
import {comingSoon} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {
  type PipelineTask,
  type TaskAction,
} from '@internal/pages/group-pipeline/detail/taskActions';

type DialogMode = 'skip' | 'sendBack' | 'changeCompletedDate' | 'rename' | 'changeDueDate';

const DIALOG_COPY: Record<DialogMode, {title: string; description: string; confirm: string}> = {
  skip: {
    title: 'Skip this step',
    description: 'Skipped steps no longer block the steps after them. The reason is logged.',
    confirm: 'Skip step',
  },
  sendBack: {
    title: 'Send back',
    description: 'Reopens this step as the next attempt and resets its due date.',
    confirm: 'Send back',
  },
  changeCompletedDate: {
    title: 'Change completed date',
    description: 'Use the date the step was actually done. The change is logged.',
    confirm: 'Save date',
  },
  rename: {
    title: 'Rename',
    description: 'Changes the name for this group only. The change is logged.',
    confirm: 'Rename',
  },
  changeDueDate: {
    title: 'Change the due date',
    description: 'For this group only. The change is logged.',
    confirm: 'Save date',
  },
};

// Runs a task action for one group's page. Wired actions call the API; the rest are placeholders.
export function useTaskActions(vbaGroupId: string) {
  const canManage = useHasPermission(CODE_PERMISSIONS.groupPipeline.manage.key);
  const utils = trpc.useUtils();
  const [dialog, setDialog] = useState<{mode: DialogMode; task: PipelineTask} | null>(null);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');

  const options = {
    onSuccess: () => {
      setDialog(null);
      return Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupPipeline.boardColumn.invalidate(),
      ]);
    },
    onError: createMutationErrorHandler('Could not update the step'),
  };
  const complete = trpc.internal.groupPipeline.task.complete.useMutation(options);
  const skip = trpc.internal.groupPipeline.task.skip.useMutation(options);
  const reopen = trpc.internal.groupPipeline.task.reopen.useMutation(options);
  const sendBack = trpc.internal.groupPipeline.task.sendBack.useMutation(options);
  const setCompletedAt = trpc.internal.groupPipeline.task.setCompletedAt.useMutation(options);
  const rename = trpc.internal.groupPipeline.task.rename.useMutation(options);
  const setDueAt = trpc.internal.groupPipeline.task.setDueAt.useMutation(options);

  const isPending =
    complete.isPending ||
    skip.isPending ||
    reopen.isPending ||
    sendBack.isPending ||
    setCompletedAt.isPending ||
    rename.isPending ||
    setDueAt.isPending;

  const openDialog = (mode: DialogMode, task: PipelineTask) => {
    setReason('');
    const initialDate = mode === 'changeDueDate' ? task.dueAt : task.completedAt;
    setDate(initialDate ? format(initialDate, 'yyyy-MM-dd') : '');
    setLabel(task.label);
    setDialog({mode, task});
  };

  const run = (task: PipelineTask, action: TaskAction) => {
    if (action.placeholder) return comingSoon(action.label)();
    switch (action.key) {
      case 'complete':
      case 'passed':
      case 'markComplete':
        return complete.mutate({taskId: task.id});
      case 'reopen':
        return reopen.mutate({taskId: task.id});
      case 'failed':
      case 'sendBack':
        return openDialog('sendBack', task);
      case 'skip':
        return openDialog('skip', task);
      case 'changeCompletedDate':
      case 'rename':
      case 'changeDueDate':
        return openDialog(action.key, task);
    }
  };

  const confirm = () => {
    if (!dialog) return;
    const taskId = dialog.task.id;
    if (dialog.mode === 'skip') skip.mutate({taskId, reason: reason.trim()});
    if (dialog.mode === 'sendBack') sendBack.mutate({taskId, reason: reason.trim() || undefined});
    // Noon local time keeps the picked day from shifting across time zones.
    if (dialog.mode === 'changeCompletedDate') {
      setCompletedAt.mutate({taskId, completedAt: new Date(`${date}T12:00:00`)});
    }
    if (dialog.mode === 'changeDueDate') {
      setDueAt.mutate({taskId, dueAt: new Date(`${date}T12:00:00`)});
    }
    if (dialog.mode === 'rename') rename.mutate({taskId, label: label.trim()});
  };

  const canConfirm =
    dialog?.mode === 'skip'
      ? reason.trim().length > 0
      : dialog?.mode === 'changeCompletedDate' || dialog?.mode === 'changeDueDate'
        ? date.length > 0
        : dialog?.mode === 'rename'
          ? label.trim().length > 0
          : true;

  const copy = dialog ? DIALOG_COPY[dialog.mode] : null;

  const dialogElement = (
    <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
      {dialog && copy && (
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>
              {copy.title}: {dialog.task.label}
            </DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          {dialog.mode === 'changeCompletedDate' ? (
            <div className='space-y-2'>
              <Label htmlFor='task-completed-date'>Completed on</Label>
              <Input
                id='task-completed-date'
                type='date'
                value={date}
                max={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          ) : dialog.mode === 'changeDueDate' ? (
            <div className='space-y-2'>
              <Label htmlFor='task-due-date'>Due on</Label>
              <Input
                id='task-due-date'
                type='date'
                value={date}
                min={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          ) : dialog.mode === 'rename' ? (
            <div className='space-y-2'>
              <Label htmlFor='task-label'>Name</Label>
              <Input
                id='task-label'
                autoFocus
                value={label}
                maxLength={120}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
          ) : (
            <div className='space-y-2'>
              <Label htmlFor='task-reason'>
                Reason{dialog.mode === 'sendBack' ? ' (optional)' : ''}
              </Label>
              <Textarea
                className='bg-white'
                id='task-reason'
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button disabled={!canConfirm || isPending} onClick={confirm}>
              {copy.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );

  return {canManage, run, isPending, dialog: dialogElement};
}
